<template>
    <div>
        <kgr-note-box
            :text-key="stocktakingNote"
            is-translated
        />
        <template v-if="null === stocktakingId || stocktakingId === ''">
            <br />
            <kgr-note-box text-key="club.member.stocktaking.stocktakingIdNotSet" />
        </template>
        <br />
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock date-center">
                <div class="kgr-grid__cell">
                    <div class="kgr-inputGroup__label">
                        {{ $t(`club.member.stocktaking.${province}.dueDate`) }}
                    </div>
                    <kgr-generic-select
                        :options="availableYears"
                        :value="selectedYear"
                        @post-input="onYearSelected"
                    />
                </div>
            </div>
        </div>

        <kgr-form-scaffold
            :pending="pendingStocktaking || pendingMembers"
            :with-back-button="false"
            :save-button-label="$t(`club.member.stocktaking.${province}.buttonDownload`)"
            @primaryBtnClicked="download"
        >
            <kgr-paginator
                v-if="!pendingMembers && !pendingCount"
                v-model="paginator"
                :length="unconsideredMembersCount"
            />

            <kgr-stocktaking-unconsidered-member-provider
                :club-id="clubId"
                :date="dueDate"
                :limit="paginator.limit || 10"
                :offset="paginator.offset"
                :callback="onUnconsideredMembersLoaded"
            >
                <kgr-stocktaking-unconsidered-member-count-provider
                    :club-id="clubId"
                    :date="dueDate"
                    :callback="onUnconsideredMembersCountLoaded"
                >
                    <div>
                        <div v-if="!pendingMembers && !pendingCount && unconsideredMembers.length > 0">
                            <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
                                {{ $t('club.member.stocktaking.unconsideredMembers') }}
                            </h2>
                            <table class="kgr-table kgr-table--block">
                                <thead>
                                    <tr class="kgr-table__row kgr-table__row--top">
                                        <th
                                            class="kgr-table__cell kgr-table__cell--headline"
                                            v-text="$t('member.membershipNumber')"
                                        ></th>
                                        <th
                                            class="kgr-table__cell kgr-table__cell--headline"
                                            v-text="$t('member')"
                                        ></th>
                                        <th
                                            class="kgr-table__cell kgr-table__cell--headline"
                                            v-text="$t('member.dateOfBirth')"
                                        ></th>
                                        <th
                                            class="kgr-table__cell kgr-table__cell--headline"
                                            v-text="$t('gender')"
                                        ></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="member in unconsideredMembers"
                                        :key="member.id"
                                        class="kgr-table__row kgr-table__row--top"
                                    >
                                        <td class="kgr-table__cell">
                                            {{ member.membershipNumber }}
                                        </td>
                                        <td class="kgr-table__cell">
                                            <a
                                                :href="getUrlForMemberEdit(member.id)"
                                                class="kgr-link kgr-link--unobtrusive"
                                            >{{ member.fullname }}</a>
                                        </td>
                                        <td class="kgr-table__cell">
                                            <kgr-fill-in
                                                v-if="!member.dateOfBirth"
                                                :href="getUrlForMemberEdit(member.id)"
                                                title="club.member.stocktaking.pleaseComplete"
                                            />
                                            {{ member.dateOfBirth }}
                                        </td>
                                        <td class="kgr-table__cell">
                                            <kgr-fill-in
                                                v-if="invalidGender(member.gender)"
                                                :href="getUrlForMemberEdit(member.id)"
                                                :title="genderNote"
                                            />
                                            {{ $t(member.gender) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </kgr-stocktaking-unconsidered-member-count-provider>
            </kgr-stocktaking-unconsidered-member-provider>

            <div class="kgr-row">
                <kgr-member-stocktaking-provider
                    :club-id="clubId"
                    :date="dueDate"
                    :callback="onStocktakingLoaded"
                >
                    <div v-if="!pendingStocktaking">
                        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
                            {{ $t('club.member.stocktaking.csvPreview') }}
                        </h2>
                        <table class="kgr-table kgr-table--block">
                            <thead>
                                <tr class="kgr-table__row kgr-table__row--top">
                                    <th
                                        v-for="head in stocktaking.header"
                                        :key="head"
                                        class="kgr-table__cell kgr-table__cell--headline"
                                    >
                                        {{ head }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(row, index) in stocktaking.values"
                                    :key="index"
                                    class="kgr-table__row kgr-table__row--top"
                                >
                                    <td
                                        v-for="(value, index2) in row"
                                        :key="index2"
                                        class="kgr-table__cell"
                                    >
                                        {{ value }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </kgr-member-stocktaking-provider>
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import FillIn from '../../components/common/FillInLink.vue';
import FormScaffold from '../../components/common/form/FormScaffold.vue';
import GenericSelect from '../../components/common/form/select/GenericSelect.vue';
import KgrPaginator from '../../components/common/Paginator.vue';
import Member from '../../js/dto/Member.js';
import MemberRepository from '@legacy/js/repository/MemberRepository.js';
import MemberStocktakingProvider from '../../components/data-provider/MemberStocktakingProvider.vue';
import NoteBox from '../../components/common/NoteBox.vue';
import StocktakingUnconsideredMemberCountProvider from '../../components/data-provider/StocktakingUnconsideredMemberCountProvider.vue';
import StocktakingUnconsideredMemberProvider from '../../components/data-provider/StocktakingUnconsideredMemberProvider.vue';

const PROVINCE_HAMBURG = 6;
const PROVINCE_HESSE = 7;

export default {
    components: {
        'kgr-member-stocktaking-provider': MemberStocktakingProvider,
        'kgr-stocktaking-unconsidered-member-provider': StocktakingUnconsideredMemberProvider,
        'kgr-stocktaking-unconsidered-member-count-provider': StocktakingUnconsideredMemberCountProvider,
        'kgr-form-scaffold': FormScaffold,
        'kgr-note-box': NoteBox,
        'kgr-generic-select': GenericSelect,
        'kgr-fill-in': FillIn,
        'kgr-paginator': KgrPaginator,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        stocktakingId: {
            type: String,
            required: true,
            default: '',
        },
        province: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            pendingMembers: true,
            pendingStocktaking: true,
            pendingCount: true,
            paginator: {
                limit: 10,
                offset: 0,
            },
            stocktaking: null,
            unconsideredMembers: null,
            unconsideredMembersCount: null,
            selectedYear: null,
        };
    },
    computed: {
        stocktakingNote() {
            return `${this.$t(`club.member.stocktaking.${this.province}.note`)
            }<br><br>`
            + `<span class="kgr-text--bold">${this.$t('club.member.stocktaking.numberNote')}</span>`;
        },
        availableYears() {
            const currentYear = new Date().getFullYear();
            return [currentYear - 1, currentYear, currentYear + 1];
        },
        dueDate() {
            if (this.province === PROVINCE_HAMBURG) {
                return `${this.selectedYear.toString()}-10-01`;
            }
            return `${this.selectedYear.toString()}-01-01`;
        },
        genderNote() {
            if (this.province === PROVINCE_HESSE || this.province === PROVINCE_HAMBURG) {
                return 'club.member.stocktaking.pleaseComplete';
            }
            return 'club.member.stocktaking.genderNote';
        },
    },
    created() {
        this.selectedYear = new Date().getFullYear();
    },
    methods: {
        invalidGender(gender) {
            if (this.province === PROVINCE_HESSE || this.province === PROVINCE_HAMBURG) {
                return gender === null;
            }
            return gender !== Member.GENDER_MALE && gender !== Member.GENDER_FEMALE && gender !== Member.GENDER_DIVERSE;
        },
        onYearSelected(date) {
            this.selectedYear = date;
        },
        onStocktakingLoaded(stocktaking) {
            this.stocktaking = stocktaking;
            this.pendingStocktaking = false;
        },
        onUnconsideredMembersLoaded(unconsideredMembers) {
            this.unconsideredMembers = unconsideredMembers;
            this.pendingMembers = false;
        },
        onUnconsideredMembersCountLoaded(unconsideredMembersCount) {
            this.unconsideredMembersCount = unconsideredMembersCount;
            this.pendingCount = false;
        },
        download() {
            MemberRepository.getStocktakingFile(this.clubId, this.selectedYear)
                .catch((error) => {
                    if (error.status === 400 && error.message) {
                        this.$notify.warning(error.message);
                    } else {
                        this.$notify.error('error.general');
                    }
                });
        },
        getUrlForMemberEdit(memberId) {
            return `/backend/clubs/members/${memberId}/edit/basicData`;
        },
    },
};
</script>
